/**
 * Privacy filter for the web app's Sentry events (services/monitoring.js).
 * Same rules as the desktop app's src/monitoring/scrub.ts and the backend's
 * core/monitoring.py: an ALLOWLIST of metadata leaves the browser, nothing
 * else.
 *
 * Kept: event codes, exception TYPES and stack frames (bundle file names,
 * functions, lines), short code-like values for a fixed set of keys,
 * browser/OS versions, the page path with ids replaced by ":id".
 * Removed: exception messages (they can echo server text or user input),
 * DOM/clicked-element text, console output, request URLs' query strings
 * and fragments, cookies, headers, bodies, user identity, IP address,
 * transcripts, answers, documents, payment details.
 */

export const SAFE_KEYS = new Set([
  'context', 'category', 'status', 'code', 'stage', 'kind', 'reason', 'outcome',
  'componentStack', 'window', 'route', 'attempt',
]);

const SAFE_VALUE = /^[A-Za-z0-9_.:\-/]{1,64}$/;
const CODE = /^[A-Z][A-Z0-9_]{2,63}$/;
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const LONG_NUMBER = /\b\d{5,}\b/g;
const REDACTED = '[redacted]';

const SAFE_TAG_KEYS = new Set([
  // Ours use underscores: dotted names (error.*, os.*) collide with Sentry's
  // built-in search fields.
  'krishvaq_event', 'error_kind', 'error_category', 'ui_context', 'environment', 'release',
  'handled', 'mechanism', 'browser', 'browser.name', 'os', 'os.name', 'level', 'url',
]);

const DROPPED_BREADCRUMB_CATEGORIES = new Set([
  'console', 'fetch', 'xhr', 'navigation', 'ui.click', 'ui.input', 'ui.keypress', 'http',
]);

const CONTEXT_ALLOWLIST = {
  os: new Set(['name', 'version', 'type']),
  browser: new Set(['name', 'version', 'type']),
  device: new Set(['family', 'model', 'type']),
  react: new Set(['componentStack']),
};

export function isCode(value) {
  return typeof value === 'string' && CODE.test(value);
}

function safeScalar(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && SAFE_VALUE.test(value)) return value;
  return undefined;
}

/** A URL without query string or fragment, with ids replaced by ":id". */
export function sanitizeUrl(url) {
  if (typeof url !== 'string') return undefined;
  const bare = url.split('#', 1)[0].split('?', 1)[0];
  return bare.replace(UUID, ':id').replace(LONG_NUMBER, ':id');
}

export function safeFields(input) {
  const fields = {};
  if (!input || typeof input !== 'object') return fields;
  Object.keys(input).forEach((key) => {
    if (!SAFE_KEYS.has(key)) return;
    if (key === 'componentStack' && typeof input[key] === 'string') {
      fields[key] = input[key].slice(0, 2000);
      return;
    }
    const safe = safeScalar(input[key]);
    if (safe !== undefined) fields[key] = safe;
  });
  return fields;
}

function scrubFrames(frames) {
  if (!Array.isArray(frames)) return [];
  return frames.slice(-60).map((frame) => {
    const clean = {};
    if (frame.filename !== undefined) clean.filename = sanitizeUrl(frame.filename);
    if (frame.abs_path !== undefined) clean.abs_path = sanitizeUrl(frame.abs_path);
    if (typeof frame.function === 'string') clean.function = frame.function.slice(0, 120);
    if (typeof frame.lineno === 'number') clean.lineno = frame.lineno;
    if (typeof frame.colno === 'number') clean.colno = frame.colno;
    if (typeof frame.in_app === 'boolean') clean.in_app = frame.in_app;
    return clean;
  });
}

function safeMessage(message) {
  if (typeof message !== 'string') return message;
  return CODE.test(message) ? message : REDACTED;
}

export function scrubBreadcrumb(crumb) {
  if (!crumb || typeof crumb !== 'object') return null;
  const category = typeof crumb.category === 'string' ? crumb.category : '';
  if (DROPPED_BREADCRUMB_CATEGORIES.has(category)) return null;
  const clean = {
    category: category.slice(0, 64),
    level: crumb.level,
    type: crumb.type,
    timestamp: crumb.timestamp,
  };
  if (isCode(crumb.message)) clean.message = crumb.message;
  if (crumb.data && typeof crumb.data === 'object') clean.data = safeFields(crumb.data);
  return clean;
}

/** Sentry beforeSend. Returns the cleaned event, or null to drop it. */
export function scrubEvent(event) {
  try {
    delete event.user;
    delete event.server_name;
    if (event.request && typeof event.request === 'object') {
      const url = sanitizeUrl(event.request.url);
      event.request = url ? { url } : {};
    }
    if (event.message !== undefined) event.message = safeMessage(event.message);
    if (event.logentry) {
      event.logentry.message = safeMessage(event.logentry.message);
      delete event.logentry.params;
      delete event.logentry.formatted;
    }
    ((event.exception && event.exception.values) || []).forEach((value) => {
      if (typeof value.value === 'string' && !CODE.test(value.value.trim())) value.value = REDACTED;
      if (value.stacktrace) value.stacktrace.frames = scrubFrames(value.stacktrace.frames);
    });
    event.extra = safeFields(event.extra);
    if (event.tags && typeof event.tags === 'object') {
      const tags = {};
      Object.keys(event.tags).forEach((key) => {
        const value = key === 'url' ? sanitizeUrl(event.tags[key]) : event.tags[key];
        const safe = safeScalar(value);
        if (SAFE_TAG_KEYS.has(key) && safe !== undefined) tags[key] = safe;
      });
      event.tags = tags;
    }
    if (event.contexts && typeof event.contexts === 'object') {
      const contexts = {};
      Object.keys(event.contexts).forEach((name) => {
        const allowed = CONTEXT_ALLOWLIST[name];
        const value = event.contexts[name];
        if (!allowed || !value || typeof value !== 'object') return;
        const kept = {};
        Object.keys(value).forEach((key) => {
          if (!allowed.has(key)) return;
          const field = value[key];
          if (typeof field === 'string') kept[key] = field.slice(0, key === 'componentStack' ? 2000 : 120);
          else if (typeof field === 'number' || typeof field === 'boolean') kept[key] = field;
        });
        contexts[name] = kept;
      });
      event.contexts = contexts;
    }
    if (Array.isArray(event.breadcrumbs)) {
      event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb).filter(Boolean);
    }
    if (typeof event.transaction === 'string') event.transaction = sanitizeUrl(event.transaction);
    delete event.attachments;
    return event;
  } catch (_) {
    return null;
  }
}

/** Bounded per-key rate limit. */
export function createRateLimiter(windowMs, maxKeys = 200) {
  const last = new Map();
  return function allow(key, now = Date.now()) {
    const previous = last.get(key);
    if (previous !== undefined && now - previous < windowMs) return false;
    last.delete(key);
    last.set(key, now);
    while (last.size > maxKeys) last.delete(last.keys().next().value);
    return true;
  };
}

/** Integrations the web SDK must not run (privacy). */
export const DROPPED_INTEGRATIONS = new Set([
  'Breadcrumbs', // DOM click text, console, fetch/xhr URLs, history
  'BrowserTracing',
  'Replay',
  'ReplayCanvas',
  'Feedback',
]);
